import { useState } from 'react';
import { Button, Label, ListBox, ListBoxItem, Select } from '@heroui/react';

import Notice from './Notice';
import StatusChip from './StatusChip';
import userService from '../../services/auth.service';
import { RELATED, messageOf, SELECT_PLACEHOLDER } from '../../i18n/el';

// ΛΑ-12: οι συσχετίσεις ενός πόρου, μέσα στο παράθυρό του.
//
// **Δεν είναι τέταρτη καρτέλα, και είναι απόφαση.** Το παράθυρο έχει ήδη τρεις
// τιμές μίας κατάστασης, που αποδίδονται ως τρεις καρτέλες σε στενή οθόνη και
// δύο σε πλατιά — «ο πόρος» αριστερά, «οι κρίσεις» δεξιά. Η συσχέτιση **δεν
// είναι κρίση**: είναι δεδομένο του πόρου, όπως οι ετικέτες. Τέταρτη τιμή θα την
// έστελνε στη δεξιά στήλη, όπου δεν ανήκει, ή θα απαιτούσε **εμφωλευμένες**
// καρτέλες — που το ίδιο το παράθυρο έχει ήδη απορρίψει ρητά.
//
// ⚠ **ΔΗΛΩΜΕΝΟ ΟΡΙΟ: ο επιλογέας γεμίζει από τους πόρους της ΤΡΕΧΟΥΣΑΣ
// συλλογής.** Δεν είναι ελάττωμα και **δεν αναφέρεται ως εύρημα** — το βήμα
// ελέγχου το λέει ρητά. Η ΛΑ-12 δεν περιορίζει σε συλλογή, αλλά η διασυλλογική
// συσχέτιση απαιτεί **αναζήτηση με πληκτρολόγηση** πάνω στη διασυλλογική
// διαδρομή, δηλαδή δεύτερη οθόνη· είναι καταγεγραμμένη ως μελλοντική επέκταση.
// Ο φράχτης του εξυπηρετητή λέει ακριβώς ό,τι προσφέρει η φόρμα: στόχος εκτός
// συλλογής απορρίπτεται με 404.
//
// **Τι ΔΕΝ δείχνει η λίστα:** συσχετισμένο πόρο που ο αιτών δεν βλέπει. Το
// φιλτράρει ο εξυπηρετητής, με τον ίδιο μηχανισμό που φιλτράρει το `collections`
// του κοινού πόρου — ο σύνδεσμος είναι κοινός, η ορατότητά του όχι.

/**
 * Οι πόροι που επιτρέπεται να προσφέρει ο επιλογέας.
 *
 * **Καθαρή συνάρτηση και εξαγόμενη**, γιατί ο κανόνας δεν είναι παρατηρήσιμος
 * αλλιώς: οι επιλογές ενός `Select` αποδίδονται σε **πύλη**, οπότε η απόδοση σε
 * συμβολοσειρά δεν τις βλέπει και ένας ισχυρισμός πάνω στη σήμανση θα ήταν
 * πράσινος ό,τι κι αν έκανε το φίλτρο.
 *
 * Ο κανόνας: ούτε ο **ίδιος** ο πόρος, ούτε ό,τι είναι **ήδη** συσχετισμένο —
 * επιλογή που ο εξυπηρετητής θα απέρριπτε είναι κουμπί που υπόσχεται κάτι
 * ανύπαρκτο.
 */
export function candidatesOf(resource, resources = []) {
    const related = (resource?.relatedResources ?? []).map(String);

    return resources.filter(
        (item) => String(item.id) !== String(resource?.id) && !related.includes(String(item.id))
    );
}

export default function RelatedResources({
    resource,
    collectionId,
    resources = [],
    canEdit = false,
    onChanged = () => {}
}) {
    const [choice, setChoice] = useState('');
    const [isSaving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const relatedIds = (resource.relatedResources ?? []).map(String);

    // Τα αναγνωριστικά είναι ό,τι επιστρέφει ο εξυπηρετητής· τα **ονόματα** τα
    // έχει ήδη η σελίδα, από τη λίστα της συλλογής. Δεύτερο αίτημα ανά πόρο θα
    // ήταν N κλήσεις για κείμενο που είναι ήδη στη μνήμη.
    const byId = new Map(resources.map((item) => [String(item.id), item]));

    const related = relatedIds.map((id) => ({ id, item: byId.get(id) ?? null }));

    // Ο επιλογέας δεν προσφέρει ούτε τον ίδιο τον πόρο ούτε ό,τι είναι ήδη
    // συσχετισμένο: επιλογή που ο εξυπηρετητής θα απέρριπτε είναι κουμπί που
    // υπόσχεται κάτι ανύπαρκτο.
    const candidates = candidatesOf(resource, resources);

    function dismissError() {
        setError('');
    }

    function add() {
        if (!choice) return;

        setSaving(true);
        setError('');

        userService.relateResource(collectionId, resource.id, choice).then(
            () => {
                setChoice('');
                setSaving(false);
                onChanged();
            },
            (apiError) => {
                setError(messageOf(apiError));
                setSaving(false);
            }
        );
    }

    function makeRemove(relatedId) {
        return function remove() {
            setError('');
            userService.unrelateResource(collectionId, resource.id, relatedId).then(
                onChanged,
                (apiError) => setError(messageOf(apiError))
            );
        };
    }

    return (
        <section className="flex flex-col gap-3 border-t border-border pt-3">
            <div className="flex flex-col gap-1">
                <h4 className="text-sm font-medium">{RELATED.title}</h4>
                <p className="text-xs text-muted">{RELATED.intro}</p>
            </div>

            {error && (
                <Notice status="danger" title={RELATED.failed} onDismiss={dismissError}>
                    {error}
                </Notice>
            )}

            {related.length === 0 ? (
                <p className="text-sm text-muted">{RELATED.empty}</p>
            ) : (
                <ul className="flex flex-wrap gap-2">
                    {related.map(({ id, item }) => (
                        // ⚠ **ΤΟ ΠΛΑΤΟΣ ΕΙΝΑΙ ΦΡΑΓΜΕΝΟ, ΚΑΙ ΤΟ «Χ» ΕΙΝΑΙ Ο ΛΟΓΟΣ.**
                        // Ο τίτλος ενός πόρου δεν έχει όριο μήκους: μια πρόσκληση με
                        // ολόκληρο τον τίτλο του προγράμματος έσπρωχνε το κουμπί
                        // αφαίρεσης **έξω από την κάρτα**, και ο χρήστης έπρεπε να
                        // κυλήσει οριζόντια για να το βρει — σε διάλογο που δεν
                        // κυλά οριζόντια πουθενά αλλού.
                        //
                        // Το 95% αντί για 100%: αφήνει ορατό ότι το στοιχείο έχει
                        // **όριο**, αντί να ακουμπά την άκρη σαν να συνεχίζει.
                        <li key={id} className="flex max-w-[95%] min-w-0">
                            <span className="flex min-w-0 items-center gap-1">
                                <StatusChip icon="fa-solid fa-diagram-project" tone="info" appearance="outline">
                                    {/* Ο πόρος που δεν είναι στη φορτωμένη σελίδα δεν
                                        εμφανίζεται ως κενό: το «άλλη σελίδα» είναι
                                        πληροφορία, το κενό chip είναι σφάλμα. */}
                                    {item ? item.title : RELATED.elsewhere}
                                </StatusChip>
                                {canEdit && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onPress={makeRemove(id)}
                                        aria-label={RELATED.removeOf(item ? item.title : RELATED.elsewhere)}
                                    >
                                        <i className="fa-solid fa-xmark" aria-hidden="true" />
                                    </Button>
                                )}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {canEdit && candidates.length > 0 && (
                <div className="flex flex-wrap items-end gap-2">
                    <Select placeholder={SELECT_PLACEHOLDER}
                        selectedKey={choice}
                        onSelectionChange={setChoice}
                        className="min-w-0 flex-1"
                    >
                        <Label>{RELATED.add}</Label>
                        <Select.Trigger>
                            <Select.Value />
                            <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                            <ListBox items={candidates}>
                                {(item) => <ListBoxItem id={item.id}>{item.title}</ListBoxItem>}
                            </ListBox>
                        </Select.Popover>
                    </Select>
                    <Button size="sm" isDisabled={!choice} isPending={isSaving} onPress={add}>
                        {RELATED.confirm}
                    </Button>
                </div>
            )}

            {canEdit && candidates.length === 0 && (
                <p className="text-xs text-muted">{RELATED.noCandidates}</p>
            )}
        </section>
    );
}
